import React, { useState } from 'react';
import { money, pickupRange, tierProgress, MAX_ORDER_QUANTITY, MAX_LINE_QUANTITY } from '../shop';
import { ProductPhoto } from './Photo';

export function Cart({ summary, tiers, ready, checkout, form, setForm, dispatch, onClose }) {
  const [step, setStep] = useState(checkout.busy ? 'checkout' : 'box');
  const { pending, busy, error } = checkout;
  const range = pickupRange();
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const progress = tierProgress(summary.quantity, tiers);

  const submit = (e) => {
    e.preventDefault();
    checkout.send({
      customer: { name: form.name, email: form.email },
      pickupDate: form.pickupDate || undefined,
      note: form.note || undefined,
      items: summary.lines.map((l) => ({ id: l.product.id, quantity: l.quantity })),
    });
  };

  if (pending) {
    const items = pending.request.items || [];
    return (
      <div className="drawer-overlay" onClick={onClose}>
        <aside className="drawer" role="dialog" aria-modal="true" aria-label="Unconfirmed order" onClick={(e) => e.stopPropagation()}>
          <header className="drawer-head">
            <h2>{busy ? 'Placing your order' : 'Unconfirmed order'}</h2>
            <button type="button" className="close-btn" onClick={onClose} aria-label="Close">×</button>
          </header>
          {busy ? (
            <p>Sending your order for {items.reduce((n, i) => n + i.quantity, 0)} macarons…</p>
          ) : (
            <p>Your order for {items.reduce((n, i) => n + i.quantity, 0)} macarons ({pending.request.customer.name}) was sent, but the
              shop's answer did not arrive. Retrying sends exactly the same order: if it already went through you get it back,
              it is never placed twice.</p>
          )}
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="drawer-actions">
            <button type="button" className="link-btn" disabled={busy} onClick={checkout.discard}>Discard it</button>
            <button type="button" className="btn btn-dark" disabled={busy} onClick={() => checkout.send(null)}>{busy ? 'Checking…' : 'Retry the order'}</button>
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Your box" onClick={(e) => e.stopPropagation()}>
        <header className="drawer-head">
          <h2>{step === 'box' ? 'Your box' : 'Checkout'}</h2>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </header>
        <p className="demo-notice" role="note">
          <strong>Demo shop.</strong> Please don&rsquo;t enter real names, emails or payment details: orders here are test
          data, and nothing is baked or charged.
        </p>
        {summary.lines.length > 0 && (
          <ol className="steps" aria-label="Checkout steps">
            <li className={step === 'box' ? 'current' : 'done'}>Box</li>
            <li className={step === 'checkout' ? 'current' : ''}>Details</li>
            <li>Confirmation</li>
          </ol>
        )}
        {summary.lines.length === 0 ? (
          <div className="empty-box">
            <ProductPhoto product={{ slug: 'raspberry-rose' }} className="empty-photo" />
            <p className="empty-title">{ready ? 'Your box is empty' : 'Loading the shop’s prices…'}</p>
            {ready && <p className="muted">Add a few macarons to start. 10% off a box of 12, 15% off 24 or more.</p>}
            <button type="button" className="btn btn-outline" onClick={onClose}>Browse flavours</button>
          </div>
        ) : (
          <>
            <div className="tier-meter" aria-label="Box discount">
              <div className="meter"><span style={{ width: `${Math.round(progress.ratio * 100)}%` }} /></div>
              <p>{progress.next ? `Add ${progress.next.minQuantity - summary.quantity} more for ${progress.next.percent}% off` : 'Best price unlocked: 15% off your box'}</p>
            </div>
            {step === 'box' && (
              <ul className="cart-lines">
                {summary.lines.map(({ product: p, quantity, lineTotalCents }) => (
                  <li key={p.id}>
                    <ProductPhoto product={p} className="thumb" />
                    <span className="cart-name">{p.name}<small>{money(p.priceCents)} each</small>
                      <button type="button" className="remove" onClick={() => dispatch({ type: 'remove', id: p.id })}>Remove</button></span>
                    <span className="stepper">
                      <button type="button" aria-label={`One less ${p.name}`} onClick={() => dispatch({ type: 'set', id: p.id, quantity: quantity - 1, stock: p.stock })}>−</button>
                      <span aria-live="polite">{quantity}</span>
                      <button type="button" aria-label={`One more ${p.name}`} disabled={quantity >= Math.min(p.stock, MAX_LINE_QUANTITY)}
                        onClick={() => dispatch({ type: 'set', id: p.id, quantity: quantity + 1, stock: p.stock })}>+</button>
                    </span>
                    <span className="cart-total">{money(lineTotalCents)}</span>
                  </li>
                ))}
              </ul>
            )}
            <dl className="totals">
              <dt>{summary.quantity} macarons</dt><dd>{money(summary.subtotalCents)}</dd>
              {summary.discountCents > 0 && (<><dt>{summary.tier.label} ({summary.tier.percent}% off)</dt><dd>−{money(summary.discountCents)}</dd></>)}
              <dt className="grand">Total</dt><dd className="grand">{money(summary.totalCents)}</dd>
            </dl>
            {summary.tooMany && <p className="form-error">At most {MAX_ORDER_QUANTITY} macarons per order online; contact the shop for events.</p>}
            {step === 'box' ? (
              <div className="drawer-actions">
                <button type="button" className="link-btn" onClick={() => dispatch({ type: 'clear' })}>Empty the box</button>
                <button type="button" className="btn btn-dark" disabled={summary.tooMany || !ready} onClick={() => setStep('checkout')}>Checkout</button>
              </div>
            ) : (
              <form className="checkout" onSubmit={submit}>
                <p className="demo-notice compact" role="note">Use a made-up name and an address like you@example.com.</p>
                <label>Name<input required maxLength={100} value={form.name} onChange={set('name')} autoComplete="name" /></label>
                <label>Email<input required type="email" maxLength={254} value={form.email} onChange={set('email')} autoComplete="email" /></label>
                <label>Pickup date (optional)<input type="date" min={range.min} max={range.max} value={form.pickupDate} onChange={set('pickupDate')} /></label>
                <label>Note for the kitchen (optional)<textarea maxLength={500} rows={3} value={form.note} onChange={set('note')} placeholder="Gift message, allergies…" /></label>
                {error && <p className="form-error" role="alert">{error}</p>}
                <div className="drawer-actions">
                  <button type="button" className="link-btn" onClick={() => setStep('box')}>Back to the box</button>
                  <button type="submit" className="btn btn-dark" disabled={busy || summary.tooMany || !ready}>{busy ? 'Placing…' : `Place order · ${money(summary.totalCents)}`}</button>
                </div>
                <p className="muted reassure">Pay at pickup, nothing to pay online. Prices are confirmed by the shop when you order.</p>
              </form>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
